import React, { useEffect, useState } from "react";
import { CloudUpload, DatabaseBackup, FileUp, Loader } from "lucide-react";
import Spacing from "../Spacing";
import Card from "../Card";
import Select from "react-select";
import { getPractice, notify } from "../../helpers";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import ConfirmationFileUploadModal from "../Modal/ConfirmationFileUploadModal";
import { syncIpaData, updateIpaData } from "../../services/workerServices";
import IPAImportViewModal from "../Modal/IPAImportViewModal";

export default function IPADataImport({selectedPracticeChange, fetchChangePracticeData}) {
  let account_id = getPractice("account_id");
  let location_id = getPractice("location_id");
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    account_id = selectedPracticeChange?.account_id;
    location_id = selectedPracticeChange?.location_id;
  }

  const defaultValues = {
    account_id: account_id,
    location_id: location_id,
    insurance_type: "",
    file: null
  };
  const [formData, setFormData] = useState(defaultValues);
  const [uploadIpaDisabled, setUploadIpaDisabled] = useState(true);
  const [uploadIpaLoader, setUploadIpaLoader] = useState(false);
  const [ipaFile, setIpaFile] = useState(null);
  const [fileIpaError, setIpaFileError] = useState(false);
  const [ipaFileUploadConfirmation, setIpaFileUploadConfirmation] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const [allIpaData, setAllIpaData] = useState([]);
  const [viewIpaDataInfo, setViewIpaDataInfo] = useState(false);
  const [ipaEditLoader, setIpaEditLoader] = useState(false);

  const [insuranceType] = useState([
    { value: "Medicaid", label: "Medicaid" },
  ]);

  useEffect(() => {
    setFormData({
      ...formData,
      account_id: account_id,
      location_id: location_id,
      insurance_type: "",
      file: null
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const handleIpaFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    if (
      selectedFile &&
      ![
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
        "application/csv",
      ].includes(selectedFile.type)
    ) {
      setIpaFileError(true);
      setUploadIpaDisabled(true);
      setIpaFile(null);
      setFormData({
        ...formData,
        file: null
      });
    } else {
      setIpaFileError(false);
      setUploadIpaDisabled(false);
      setIpaFile(selectedFile);
      setFormData({
        ...formData,
        file: selectedFile
      });
    }
  };

  const handelShowWarning = async (e) => {
    e.preventDefault();
    setIpaFileUploadConfirmation(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  };

  const handelSubmitIpaData = async (e) => {
    setUploadIpaDisabled(true);
    setUploadIpaLoader(true);

    try {
      const response = await syncIpaData(formData);
      
      if (response?.detail) {
        setErrorMessage(response?.detail);
      } else { 
        if(response?.data?.status === 'success'){
          notify('success', response?.data?.message);
          setAllIpaData(response?.data?.data)
          setViewIpaDataInfo(true);
          setPreviousScrollPosition(window.scrollY);
          window.scrollTo(0, 0);
          setIpaFileUploadConfirmation(false);
          setErrorMessage(null);
          setFormData(defaultValues);
        } else {
          setErrorMessage(response?.data?.message);
        }
      } 
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        setErrorMessage(error?.response?.data?.detail);
      }
    } finally { 
      setUploadIpaDisabled(false);
      setUploadIpaLoader(false);
    }
  };

  const handelUpdateIpaData = async (data) => {
    setIpaEditLoader(true);

    try {
      const response = await updateIpaData(data);
      
      if (response?.detail) {
        notify("error", response?.detail);
      } else { 
        if(response?.data?.status === 'success'){
          notify("success", response?.data?.message, 7);
          setAllIpaData([]);
          setViewIpaDataInfo(false);
          window.scrollTo(0, previousScrollPosition);
        } else {
          notify("error", response?.data?.message);
        }
      } 
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail);
      }
    } finally { 
      setIpaEditLoader(false);
    }
  };

  useDocumentTitle("IPA Data Sync");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24 docpro_mb_0">
          <i className="docpro_white_color" style={{ backgroundColor: '#0b68cb' }}>
            <FileUp width={18} height={18} />
          </i>
          IPA Data Sync
        </h2>
      </div>
      <Spacing lg="15" sm="15" />
      
      <div className="flex items-center" style={{ justifyContent: 'center' }}>
        <div className="docpro_sync_data_section docpro_settings_container w-full">
          <Card variant="docpro_card w-full">

            <form onSubmit={handelShowWarning} className="docpro_text_start">
              <Spacing lg="10" sm="10" />
              <div className="card_body">
                <label className="docpro_fw_500 docpro_primary_color">
                  Insurance Type
                </label>
                <Select
                  name="insurance_type"
                  options={insuranceType}
                  onChange={(selectedOption) => {
                    setFormData({ ...formData, insurance_type: selectedOption.value });
                    setUploadIpaDisabled(false);
                  }}
                  placeholder="Search..."
                  isSearchable={true}
                  value={insuranceType.find(
                    (option) => option.value === formData.insurance_type
                  )}
                  className="react-select-container w-full"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '43px',
                      borderRadius: '8px',
                      borderColor: 'var(--border)',
                    }),
                  }}
                />

                <Spacing lg="20" sm="20" />
                <label className="docpro_primary_color">
                  <span className="docpro_fw_500">IPA Data File</span> <small className="docpro_red_color">(.xlsx, .xls, .csv)</small>
                </label>
                <div
                  className="docpro_input_file_container"
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                  onDragLeave={(e) => e.currentTarget.classList.remove('dragover')}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('dragover');
                    const f = e.dataTransfer.files?.[0];
                    if (f) handleIpaFileChange({ target: { files: [f] } });
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Upload Excel/CSV file by dragging or clicking"
                >
                  <span className="docpro_file_icon" aria-hidden="true">
                    <CloudUpload height={28} width={28} />
                  </span>
                  <div className="docpro_file_text">
                    <div className="docpro_file_title">
                      {ipaFile ? ipaFile.name : 'Drag and drop IPA data file'}
                    </div>
                    <div className="docpro_file_hint">
                      {ipaFile ? '' : 'here, or click to upload'}
                    </div>
                  </div>

                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="docpro_file_hidden_input"
                    onChange={handleIpaFileChange}
                  />
                </div>
                {fileIpaError && (
                  <p className="docpro_error_message">
                    Please upload a valid Excel/CSV file.
                  </p>
                )}
              </div>

              <Spacing lg="25" sm="25" />
              <button type="submit"
                className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg w-full"
                style={{ padding: '12px 13px' }}
                disabled={uploadIpaDisabled}
              >
                <DatabaseBackup width={20} height={20} />
                <span>Sync IPA Data</span>
                {uploadIpaLoader && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
              <Spacing lg="3" sm="3" />
            </form>
          </Card>
        </div>
      </div>

      {ipaFileUploadConfirmation && (
        <ConfirmationFileUploadModal 
          openModal={ipaFileUploadConfirmation}
          setOpenModal={setIpaFileUploadConfirmation}
          previousScrollPosition={previousScrollPosition}
          uploadDisabled={uploadIpaDisabled}
          uploadLoader={uploadIpaLoader}
          modalAction={handelSubmitIpaData}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          fileName={ipaFile}
          uploadType="ipa"
        />
      )}

      {viewIpaDataInfo && (
        <IPAImportViewModal 
          openModal={viewIpaDataInfo}
          setOpenModal={setViewIpaDataInfo}
          previousScrollPosition={previousScrollPosition}
          enableLoader={ipaEditLoader}
          modalAction={handelUpdateIpaData}
          data={allIpaData}
          account_id={account_id}
          location_id={location_id}
        />
      )}
    </>
  );
}